import React, { useMemo, useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";

import {
  Mail,
  Phone,
  Twitter,
  Instagram,
  Facebook,
  Globe,
  FileText,
  Users,
  MapPin,
  Calendar,
  Link as LinkIcon,
} from "lucide-react";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/vercel/card";
import useGroups from "../../../hooks/useGroups";
import { Badge } from "../../../components/vercel/badge";

function getDomain(url, subdomain) {
  subdomain = subdomain || false;

  url = url.replace(/(https?:\/\/)?(www.)?/i, "");

  if (!subdomain) {
    url = url.split(".");

    url = url.slice(url.length - 2).join(".");
  }

  if (url.indexOf("/") !== -1) {
    return url.split("/")[0];
  }

  return url;
}

export default function Component() {
  const params = useParams();
  const groups = useGroups();

  const groupData = useMemo(() => {
    const { groupId } = params;
    let finalGroup = {};

    if (groups) {
      finalGroup = groups.find((group) => group.id === groupId);
    }

    return finalGroup || {};
  }, [params, groups]);

  return (
    <div
      className="container mx-auto px-4 py-8"
      style={{ marginTop: "5rem", marginBottom: "4rem", padding: "1rem" }}
    >
      <div className="flex flex-col md:flex-row items-center mb-8 gap-8">
        <div>
          <h1 className="text-3xl font-bold">{groupData.name}</h1>
          <p className="text-xl text-muted-foreground">
            {groupData.university}
          </p>
          {groupData.associatedGroup && (
            <p className="text-lg text-muted-foreground">
              {groupData.associatedGroup}
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Información del grupo</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {groupData.campus && (
                <li className="flex items-center">
                  <MapPin className="mr-2 h-4 w-4" />
                  <span>{groupData.campus}</span>
                </li>
              )}
              {groupData.creationDate && (
                <li className="flex items-center">
                  <Calendar className="mr-2 h-4 w-4" />
                  <span>
                    Fundación:{" "}
                    {new Date(groupData.creationDate).toLocaleDateString()}
                  </span>
                </li>
              )}
              {groupData.teamSize && (
                <li className="flex items-center">
                  <Users className="mr-2 h-4 w-4" />
                  <span>{groupData.teamSize} personas</span>
                </li>
              )}
              {groupData.cvLink && (
                <li className="flex items-center">
                  <FileText className="mr-2 h-4 w-4" />
                  <a
                    href={groupData.cvLink}
                    className="text-primary hover:underline"
                  >
                    CV Grupo
                  </a>
                </li>
              )}
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Investigación</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4">{groupData.description}</p>
            {groupData.knowledgeAreas && (
              <div className="mb-4">
                <h3 className="text-lg font-semibold mb-2">
                  Áreas de investigación:
                </h3>
                <div className="flex flex-wrap gap-2">
                  {groupData.knowledgeAreas.map((area, index) => (
                    <Badge key={index} variant="secondary">
                      {area}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {groupData.researchNetworks && (
              <div>
                <h3 className="text-lg font-semibold mb-2">
                  Redes de investigación:
                </h3>
                <ul className="list-disc list-inside">
                  {groupData.researchNetworks.map((network, index) => (
                    <li key={index}>{network}</li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Investigadores principales</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {groupData.mainResearcher && (
                <div>
                  <h3 className=" font-semibold text-lg">
                    {groupData.mainResearcher}
                  </h3>
                  <p className="flex items-center">
                    <Mail className="mr-2 h-4 w-4" />
                    <a
                      href={`mailto:${groupData.mainResearcherEmail}`}
                      className="text-primary hover:underline"
                    >
                      {groupData.mainResearcherEmail}
                    </a>
                  </p>
                </div>
              )}
              {groupData.secondResearcher && (
                <div>
                  <h3 className="text-lg font-semibold">
                    {groupData.secondResearcherName}
                  </h3>
                  <p className="flex items-center">
                    <Mail className="mr-2 h-4 w-4" />
                    <a
                      href={`mailto:${groupData.secondResearcherEmail}`}
                      className="text-primary hover:underline"
                    >
                      {groupData.secondResearcherEmail}
                    </a>
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Servicios Profesionales y Recursos</CardTitle>
          </CardHeader>
          <CardContent>
            {groupData.labMaterials && (
              <div className="mb-4">
                <h3 className="font-semibold mb-2 text-lg">Material:</h3>
                <ul className="list-disc list-inside">
                  {groupData.labMaterials.map((material, index) => (
                    <li key={index}>{material}</li>
                  ))}
                </ul>
              </div>
            )}
            {groupData.services && (
              <div className="mb-4">
                <h3 className="font-semibold mb-2 text-lg">
                  Servicios Profesionales:
                </h3>
                <ul className="list-disc list-inside">
                  {groupData.services.map((service, index) => (
                    <li key={index}>{service}</li>
                  ))}
                </ul>
              </div>
            )}
            {groupData.catalogueLink && (
              <a
                href={groupData.catalogueLink}
                className="flex items-center text-primary hover:underline"
              >
                <LinkIcon className="mr-2 h-4 w-4" />
                Professional Catalogue
              </a>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Información de contacto</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              {groupData.website && (
                <p className="flex items-center">
                  <Globe className="mr-2 h-4 w-4" />
                  <a
                    href={groupData.website}
                    className="text-primary hover:underline"
                  >
                    {getDomain(groupData.website)}
                  </a>
                </p>
              )}
              {groupData.email && (
                <p className="flex items-center">
                  <Mail className="mr-2 h-4 w-4" />
                  <a
                    href={`mailto:${groupData.email}`}
                    className="text-primary hover:underline"
                  >
                    {groupData.email}
                  </a>
                </p>
              )}
              {groupData.phone && (
                <p className="flex items-center">
                  <Phone className="mr-2 h-4 w-4" />
                  <a
                    href={`tel:${groupData.phone}`}
                    className="text-primary hover:underline"
                  >
                    {groupData.phone}
                  </a>
                </p>
              )}
            </div>
            <div className="space-y-2">
              {groupData.twitter && (
                <p className="flex items-center">
                  <Twitter className="mr-2 h-4 w-4" />
                  <a
                    href={`https://twitter.com/${groupData.twitter}`}
                    className="text-primary hover:underline"
                  >
                    {groupData.twitter}
                  </a>
                </p>
              )}
              {groupData.instagram && (
                <p className="flex items-center">
                  <Instagram className="mr-2 h-4 w-4" />
                  <a
                    href={`https://instagram.com/${groupData.instagram}`}
                    className="text-primary hover:underline"
                  >
                    {groupData.instagram}
                  </a>
                </p>
              )}
              {groupData.facebook && (
                <p className="flex items-center">
                  <Facebook className="mr-2 h-4 w-4" />
                  <a
                    href={`https://facebook.com/${groupData.facebook}`}
                    className="text-primary hover:underline"
                  >
                    {groupData.facebook}
                  </a>
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
